import type { NewsArticle } from './newsData';

export const newsArchive2023: NewsArticle[] = [
  {
    slug: 'essential-course-opening-guide-202303',
    date: '2023年3月11日',
    category: 'schedule',
    categoryLabel: '開校情報',
    title: 'テーブルセッティング実習で扱う道具と、教える順序に込めた意図',
    metaDescription:
      'フォーク・ナイフ・グラス・ナプキンなど、テーブルセッティング実習で実際に使う道具と、なぜ布巾から始め皿・カトラリー・グラスの順で型を積むのかを解説。定員6名の少人数で手元を確認しながら進める実技の考え方をまとめました。',
    keywords: ['テーブルセッティング', '給仕作法', 'カトラリー配置', '実技実習', '執事アカデミー'],
    heroImage: '/images/generated/essential-manners-2026.jpg',
    heroAlt: 'テーブルセッティングの型を手元で確認する実習の様子',
    figures: [
      {
        src: '/images/generated/training-basic-2026.jpg',
        alt: '布巾と皿の位置を整えながら手順を確認する手元',
        caption: '道具の名前と置き場所を一致させてから、動線のある配置へ進みます',
      },
    ],
    overview: {
      heading: 'テーブルセッティング実習の要点',
      rows: [
        { label: '主な道具', value: '皿・カトラリー・グラス・ナプキン・布巾' },
        { label: '指導の順序', value: '布巾→皿→カトラリー→グラス→最終点検' },
        { label: '授業形態', value: '1日6時間（90分×4コマ）／土日開催' },
        { label: '定員', value: '6名（最少催行3名）' },
        { label: '会場', value: 'お台場本校' },
      ],
    },
    faq: [
      {
        question: '家庭用の食器と実習用の道具は違いますか？',
        answer:
          '基本の形は家庭でも通じるものを用います。ただし間隔・向き・持ち方の基準を揃えるため、教室では寸法の分かりやすいセットで型を固定してから応用へ進みます。',
      },
      {
        question: '順序を飛ばして後半だけ練習できますか？',
        answer:
          '推奨しません。手前の工程が曖昧なまま後半へ進むと、修正に倍の時間がかかります。短い反復で土台を固めてから次の道具へ移る設計です。',
      },
    ],
    related: [
      { title: 'カリキュラム', desc: '実技科目の進み方を確認', href: '/curriculum' },
      { title: '教材について', desc: '全15冊と実習の対応関係', href: '/materials' },
      { title: 'コース一覧', desc: 'エッセンシャルからの学び', href: '/courses' },
      { title: '資料請求（無料）', desc: '実習内容の概要をお届け', href: '/brochure' },
    ],
    body: `<p>テーブルセッティングの実習では、見た目の美しさより先に「どの道具を、どの順番で、なぜ置くのか」を身体に通します。お台場本校の実技では、布巾・皿・フォークとナイフ・スプーン類・グラス・ナプキンを一式そろえ、定員6名の少人数で手元の角度まで確認しながら進めます。ここでは、教室で実際に扱う道具と、教える順序に込めた意図を整理します。</p>
<h2>実習で机上に並べる道具の役割</h2>
<p>最初に渡すのは清潔な布巾です。指紋や水滴を残さない習慣を、配置の前に体で覚えます。次にメインの皿を軸にし、フォークを左、ナイフとスプーンを右へ置く基準線をつくります。グラスは刃先の延長上に置くと動線がぶれにくいため、カトラリーが安定してから扱います。ナプキンは折り方そのものより、客席から見たときの厚みと向きを優先して調整します。教科書全15冊のうち、食卓まわりの分冊では名称と写真を対応させ、教室の実物とページを行き来できるようにしています。</p>
<h2>布巾から入る理由と、段階を分ける意味</h2>
<p>順序を布巾→皿→カトラリー→グラス→最終点検としたのは、失敗の影響範囲が小さい工程から大きい工程へ積むためです。布巾の扱いが雑なまま皿を置くと、後工程すべてに汚れとずれが伝播します。逆に皿の中心が決まれば、左右の間隔は指幅や視線のガイドで再現しやすくなります。90分×4コマの1日6時間のなかでは、午前に名称と位置、午後に立ち位置を変えた点検と修正を繰り返し、同じ配置を「自分の正面以外」からも直せる状態を目指します。</p>
<h2>少人数だからこそ見える修正ポイント</h2>
<p>最少催行3名・定員6名という規模は、講師が一人ずつ刃の向きやグラスの縁の高さまで介入できる前提です。写真だけ真似ると左右が反転したり、客側から見たバランスが崩れたりします。実習では意図的に席を入れ替え、自分がセットした席に座って違和感を言語化します。正しさを暗記するのではなく、ずれを自分で発見する回路を先に作る。そのための道具選定と順序設計です。食卓の型に課題を感じている方は、コース説明とあわせて実技の進め方も比較検討してみてください。</p>`,
  },
  {
    slug: 'seminar-trial-consultation-202307',
    date: '2023年7月15日',
    category: 'seminar',
    categoryLabel: '説明会',
    title: '未経験で入学した人が、最初の1ヶ月で積み上げる基礎の中身',
    metaDescription:
      '執事の学びを未経験から始めた受講者が、最初の4週間で何を身につけるのか。立ち居振る舞い、声の出し方、記録の残し方、出席率の目安まで、土日・1日6時間のペースに沿った現実的な到達イメージを具体的に紹介します。',
    keywords: ['未経験受講', '最初の一ヶ月', '基礎所作', '立ち居振る舞い', '執事養成'],
    heroImage: '/images/generated/about-classroom-2026.jpg',
    heroAlt: '講義室で基礎の立ち位置と姿勢を確認する受講の風景',
    figures: [
      {
        src: '/images/generated/essential-writing-2026.jpg',
        alt: '授業後に手順と気づきをノートへ整理する様子',
        caption: '動きの型と、振り返りの言葉をセットで残す習慣を最初に作ります',
      },
    ],
    overview: {
      heading: '最初の1ヶ月で扱うテーマ',
      rows: [
        { label: '想定対象', value: '未経験からエッセンシャルを検討する方' },
        { label: '学習ペース', value: '土日開催・1日6時間（90分×4コマ）' },
        { label: '重視する力', value: '姿勢／歩き方／挨拶／短い記録' },
        { label: '出席の目安', value: '出席率80%以上を意識した計画' },
        { label: '定員', value: '6名（最少催行3名）' },
      ],
    },
    faq: [
      {
        question: '仕事を続けながら最初の1ヶ月を回せますか？',
        answer:
          '講義は土日開催を基本としているため、平日勤務の方でもペースを組みやすい構成です。ただし帰宅後の短い復習（用語と手順の書き出し）を週に数回確保できると定着が早まります。',
      },
      {
        question: '1ヶ月で資格は取れますか？',
        answer:
          '最初の1ヶ月は土台づくりが中心です。協会認定資格は所定の課程と修了実技試験を経て案内されるため、短期の合格だけを目標には設計していません。',
      },
    ],
    related: [
      { title: '入学案内', desc: '申込み前に確認する条件', href: '/admission' },
      { title: 'コース一覧', desc: 'エッセンシャルの位置づけ', href: '/courses' },
      { title: '説明会・セミナー', desc: '学び始めの疑問を解消', href: '/seminar' },
      { title: 'お問い合わせ', desc: '受講ペースの相談', href: '/contact' },
    ],
    body: `<p>未経験から学びを始める方がつまずきやすいのは、技術の難易度そのものより「何から手をつければよいか分からない」状態が続くことです。最初の1ヶ月は、派手な演目を増やす期間ではなく、姿勢・歩き方・挨拶・短い記録という四つの土台を、土日の集中講義で反復する期間と位置づけています。以下は、教室で実際に求めている到達のイメージです。</p>
<h2>週ごとの焦点を分けて負荷を上げすぎない</h2>
<p>1週目は静止した姿勢と視線の安定、2週目は室内の歩き方と立ち止まり方、3週目は短い挨拶と受け答えの間、4週目は一日の動きを箇条書きで残す記録、という順に負荷を上げます。1日6時間（90分×4コマ）あるからといって、初月からすべてを同時に完璧へ持っていく必要はありません。むしろ一つのコマで直すポイントを二つまでに限り、帰宅後に用語だけを書き写す方が、翌週の再現性が上がります。</p>
<h2>「できたつもり」を減らすための観察と記録</h2>
<p>未経験の方が最初に得るべきなのは、正解の丸暗記より、自分のずれに気づく観察力です。ペアで動きを見合い、良かった点と直す点を一文ずつ交換する時間を意図的に入れています。記録は感想文ではなく、手順・所要の目安・次に試す修正の三点に絞ります。教科書全15冊は参照用の骨格として使い、教室で指摘された言葉を余白へ転記すると、後から見返したときに再現しやすくなります。</p>
<h2>出席率と体調管理もスキルのうち</h2>
<p>修了判定では出席率80%以上が目安のひとつになります。最初の1ヶ月で生活リズムに講義を組み込む練習をしておくと、後半の実技比重が増えたときに崩れにくくなります。定員6名のクラスは欠席が目立ちやすい反面、戻ったときの追いつき支援もしやすい規模です。未経験であることを不利と捉える必要はなく、初期に型と振り返りの型を持てた人ほど、その後の応用が滑らかになります。自分の生活に土日2日の集中が載るかどうかを、申込み前の判断材料にしてください。</p>`,
  },
  {
    slug: 'materials-curriculum-update-202311',
    date: '2023年11月18日',
    category: 'update',
    categoryLabel: '更新情報',
    title: '協会認定資格の3段階は何を証明するのか——アソシエイト／プロフェッショナル／マスター',
    metaDescription:
      '協会認定資格のアソシエイト・プロフェッショナル・マスターが、それぞれどの範囲の実務能力を示すのかを整理。修了実技試験、出席率80%以上、再試験1回無料といった運用条件と、3コースとの対応関係も分かりやすく説明します。',
    keywords: ['協会認定資格', 'アソシエイト', 'プロフェッショナル', 'マスター資格', '修了実技試験'],
    heroImage: '/images/generated/facilities-hero-2026.jpg',
    heroAlt: '資格取得に向けた学びを支える落ち着いた校内の空間',
    figures: [
      {
        src: '/images/generated/essential-hero-v2-2026.jpg',
        alt: '実技試験を見据えて所作を確認する講義の一場面',
        caption: '各段階は知識量ではなく、再現できる実技の範囲で区切っています',
      },
    ],
    overview: {
      heading: '協会認定資格3段階の整理',
      rows: [
        { label: 'アソシエイト', value: '基礎の所作と接遇手順を単独で再現できる' },
        { label: 'プロフェッショナル', value: '場面変化に応じた判断と連続動作ができる' },
        { label: 'マスター', value: '設計・指導・品質の維持まで担える' },
        { label: '評価の中心', value: '修了実技試験（再試験1回無料）' },
        { label: '関連コース', value: 'エッセンシャル／アドバンス／マスター' },
      ],
    },
    faq: [
      {
        question: 'コース名と資格名は同じ意味ですか？',
        answer:
          '対応関係はありますが同一ではありません。コースは学習の経路、資格は到達した実技範囲の証明です。所定の課程と修了実技試験を経て段階が付与される運用です。',
      },
      {
        question: '上位段階だけを直接受験できますか？',
        answer:
          '原則として下位で求める再現力を土台に積み上げる設計です。飛び級的な受験より、欠落した基礎を先に埋める方が試験でも実務でも安定します。',
      },
    ],
    related: [
      { title: '修了・認定', desc: '試験と資格の公式案内', href: '/certificate' },
      { title: 'コース一覧', desc: '3コースと到達目標', href: '/courses' },
      { title: '受講料金', desc: '費用面の確認', href: '/pricing' },
      { title: '資料請求（無料）', desc: '資格の段階表を含む資料', href: '/brochure' },
    ],
    body: `<p>協会認定資格を「肩書が増える仕組み」とだけ捉えると、どの段階を目指すべきか判断しづらくなります。当校が案内するアソシエイト、プロフェッショナル、マスターの三段階は、名刺用の装飾ではなく、再現できる実技の範囲を外から読み取れるようにするための区分です。以下では、各段階が何を証明し、何をまだ証明しないのかを明確に分けて説明します。</p>
<h2>三段階を分ける基準は知識量ではなく再現範囲</h2>
<p>アソシエイトは、基礎の立ち居振る舞い、定型の接遇、食卓まわりの基本配置などを、指示のもとで単独再現できる水準です。プロフェッショナルは、来客の人数や時間の変化に合わせ、手順を組み替えて連続動作を維持できる水準を指します。マスターは、現場の品質を設計し、他者へ手順を伝え、崩れを早期に検知して戻せる水準です。いずれも筆記の暗記量だけでは届かず、修了実技試験で動きと判断を確認します。</p>
<h2>コースとの対応と、試験まわりの運用</h2>
<p>学びの経路としてはエッセンシャル、アドバンス、マスターの3コースが土台になり、教科書全15冊も段階的な参照が可能な構成です。資格の付与は、所定課程の履修に加え、出席率80%以上を目安とした受講状況、修了実技試験での基準到達が前提になります。不合格時は再試験1回無料の扱いがあり、直前の詰め込みより、日常の実技で安定して出せる形を優先します。土日開催・1日6時間（90分×4コマ）のリズムは、試験前だけ生活を変える必要を減らすための枠組みでもあります。</p>
<h2>読み手が資格から期待してよいこと・いけないこと</h2>
<p>アソシエイトは「基礎を一通り自分で回せる」ことの証明であり、複雑な現場の統括までを自動的に意味しません。プロフェッショナルは変化への対応力を示しますが、組織の制度設計までを保証するものではありません。マスターは設計と指導の力量を示しますが、それ自体が特定職への採用結果を約束するものでもありません。証明範囲を正しく共有できることこそが、資格を持つ側と受け取る側の双方を守ります。段階の選び方に迷う場合は、修了・認定の案内とコース資料を並べ、自分の現在の再現範囲がどこまでかを起点に検討してください。</p>`,
  },
  {
    slug: 'advance-wine-event-class-202305',
    date: '2023年5月20日',
    category: 'schedule',
    categoryLabel: '開校情報',
    title: 'ワインサービスと催事運営を軸に据えた、アドバンス5月開校クラス',
    metaDescription:
      '2023年5月開校のアドバンスコースは、ワインサービスと小規模催事の運営手順を実技の柱に据えた2ヶ月12日間・72時間の編成です。お台場本校・定員6名、土日1日6時間で申込締切と最少催行の条件も明示します。',
    keywords: ['アドバンスコース', 'ワインサービス', '催事運営', '5月開校', '執事アカデミー'],
    heroImage: '/images/generated/advance-service-2026.jpg',
    heroAlt: 'グラスの扱いとサーブの角度を確認するアドバンス実技の様子',
    figures: [
      {
        src: '/images/generated/curriculum-overview-2026.jpg',
        alt: '催事の進行表と配膳動線をホワイトボードで整理する場面',
        caption: '提供タイミングと人員配置を紙面で固めてから、実地の通し稽古へ移ります',
      },
    ],
    overview: {
      heading: '2023年5月アドバンス開校の概要',
      rows: [
        { label: '対象コース', value: 'アドバンス（2ヶ月・12日間・72時間）' },
        { label: '開校時期', value: '2023年5月下旬〜土日開催' },
        { label: '実技の柱', value: 'ワインサービス／小規模催事の運営' },
        { label: '授業形態', value: '1日6時間（90分×4コマ）' },
        { label: '定員・会場', value: '6名（最少催行3名）／お台場本校' },
      ],
    },
    faq: [
      {
        question: 'エッセンシャル未修了でも申し込めますか？',
        answer:
          '基礎の立ち居振る舞いと食卓配置が独力で再現できることが前提です。未修の方は先にエッセンシャルで型を固めてからの接続を推奨しています。',
      },
      {
        question: '申込はいつまでにすればよいですか？',
        answer:
          '開校日の10日前を受付の目安とし、定員6名に達した時点で締め切ります。最少催行3名に満たない場合は日程を再調整のうえ個別に連絡します。',
      },
    ],
    related: [
      { title: 'コース一覧', desc: 'アドバンスの到達目標', href: '/courses' },
      { title: 'カリキュラム', desc: '2ヶ月12日間の進み方', href: '/curriculum' },
      { title: '受講料金', desc: '費用と支払方法', href: '/pricing' },
      { title: '入学案内', desc: '申込手順と必要書類', href: '/admission' },
    ],
    body: `<p>2023年5月、お台場本校でアドバンスコースの新しいクラスを開校します。期間は2ヶ月・12日間・72時間、開催は土日、1日6時間（90分×4コマ）です。今期の実技はワインサービスと小規模な催事運営を軸に組み、グラスの扱い・温度管理・提供の間だけでなく、受付から退席までの進行表づくりまでを一連の流れとして扱います。定員は6名、最少催行は3名です。</p>
<h2>今期アドバンスで厚く取る二つの領域</h2>
<p>前半はボトルの運び方、ラベルの見せ方、注ぐ量と角度、空になった器の下げ方を、席数を変えながら反復します。後半は招待人数が限られた催事を想定し、タイムテーブル、担当分担、予備の動線を受講者自身が草案し、通しで回す演習に入ります。教科書全15冊のうち飲料と催事に関する分冊を毎回の事前読了に指定し、教室では紙面で曖昧だった判断を動きに落とす時間を優先します。協会認定資格の上位段階を見据える方にも、連続動作の安定を測る材料になる構成です。</p>
<h2>学び方と一日のコマ配分</h2>
<p>午前の2コマは示範と個別修正、午後の2コマはペアおよび三人組での通し稽古に充てます。講師は定員6名の規模を活かし、注ぎ手・受け手・観察役をローテーションして、自分では見えにくい肩の力みや歩幅の乱れを言語化させます。出席率80%以上を修了の目安とし、修了実技試験ではワイン提供と催事進行の両方から課題を出します。不合格の場合は再試験1回無料です。</p>
<h2>申込の流れと締切の目安</h2>
<p>入学案内のフォームから希望日程を送り、確認の連絡後に受講料の案内へ進みます。受付は開校日の10日前を目安に締め、欠員が出た場合のみ追加で受け付けます。最少催行3名に届かないときは日程をずらすか中止とし、その時点でお知らせします。ワインと催事の実技をまとめて伸ばしたい方は、5月クラスの枠と自分の土日の空きを照合したうえで判断してください。</p>`,
  },
  {
    slug: 'essential-last-intake-202310',
    date: '2023年10月8日',
    category: 'recruitment',
    categoryLabel: '募集情報',
    title: '年内最後のエッセンシャル、1ヶ月48時間で型を揃える秋期クラス',
    metaDescription:
      '2023年10月開校のエッセンシャルコースは年内最後の入門枠です。1ヶ月8日間・48時間、土日・お台場本校・定員6名。立ち居振る舞いから食卓の基本までを固めたい方へ、締切と申込手順を具体的にまとめました。',
    keywords: ['エッセンシャルコース', '年内最後', '秋期クラス', '入門講座', '48時間'],
    heroImage: '/images/generated/admission-counter-2026.jpg',
    heroAlt: '秋期の入門クラスに向けて受講相談を受ける受付スペース',
    figures: [
      {
        src: '/images/generated/access-odaiba-2026.jpg',
        alt: 'お台場本校へ向かう経路を確認する案内表示',
        caption: '初回集合前に経路と到着時刻を合わせ、オリエンテーションをスムーズに始めます',
      },
    ],
    overview: {
      heading: '2023年10月エッセンシャル募集概要',
      rows: [
        { label: '対象コース', value: 'エッセンシャル（1ヶ月・8日間・48時間）' },
        { label: '開校時期', value: '2023年10月中旬〜土日開催' },
        { label: '位置づけ', value: '2023年内最後の入門クラス' },
        { label: '授業形態', value: '1日6時間（90分×4コマ）' },
        { label: '定員・会場', value: '6名（最少催行3名）／お台場本校' },
      ],
    },
    faq: [
      {
        question: '今から準備して間に合いますか？',
        answer:
          '開校一週間前までに申込と受講料の手続きが完了していれば参加できます。教科書全15冊のうち初月分は初回に配布するため、事前購入は不要です。',
      },
      {
        question: '次の入門クラスはいつですか？',
        answer:
          '本年はこの10月期が最後のエッセンシャル開校です。以降の入門枠は翌年の日程表で公開しますので、年内に始めたい方は今期での検討が必要です。',
      },
    ],
    related: [
      { title: '入学案内', desc: '申込条件と手続き', href: '/admission' },
      { title: 'コース一覧', desc: 'エッセンシャルの内容', href: '/courses' },
      { title: 'アクセス', desc: 'お台場本校への行き方', href: '/access' },
      { title: '資料請求（無料）', desc: '日程表付きの概要資料', href: '/brochure' },
    ],
    body: `<p>2023年10月、エッセンシャルコースの秋期クラスを開校します。今年に入ってからの入門枠としては最後の期となり、1ヶ月・8日間・48時間、土日開催、1日6時間（90分×4コマ）で進めます。会場はお台場本校、定員6名、最少催行3名です。未経験からでも扱いやすいよう、姿勢・歩き方・挨拶・食卓の基本配置に範囲を絞り、短い反復で型を揃える設計にしています。</p>
<h2>年内最後の入門期に扱う範囲</h2>
<p>初週は静止姿勢と視線、二週目は室内の移動と立ち位置、三週目は声の大きさと間、四週目は皿とカトラリーの基本配置、という四段階で負荷を上げます。教科書全15冊は参照の骨格として使い、教室で指摘された語だけを余白に転記する運用です。修了時には出席率80%以上を目安とし、修了実技試験で単独再現できるかを確認します。基準に届かない場合の再試験は1回無料です。</p>
<h2>秋期ならではの進め方</h2>
<p>日照や気温が落ち着く時期のため、厚手の上着の脱ぎ着や室内履きへの履き替えなど、来客対応の前後動作を実地に組み込みやすいのが秋期の利点です。定員6名の少人数なので、講師が一人ずつ歩幅と手元を止めながら修正できます。仕事を続けながらの受講を想定し、平日は用語の書き出しだけ、実技のまとまった練習は講義日に集中させる配分を推奨します。</p>
<h2>募集の締め切り方と申込手順</h2>
<p>受付は開校日の7日前までを原則とし、定員に達し次第クローズします。申込は入学案内から希望日を送信し、返信メールの手順に沿って受講料を納めて確定となります。最少催行3名を下回る場合は期を閉じ、希望者には翌年枠の公開タイミングを個別に伝えます。年内に基礎を一通り自分の手に置きたい方は、残席と締切日を確認のうえ手続きを進めてください。</p>`,
  },
];
